import { observer } from "mobx-react-lite";

import { Heading, Stack, TooltipHost, useTheme } from "@bps/fluent-ui";
import { Country } from "@libs/enums/country.enum.ts";
import {
  CommunicationKind,
  Permission
} from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { getUserStylesSet } from "@modules/settings/screens/users/components/UserScreens.styles.tsx";
import { formatCommunicationValue } from "@stores/core/models/Communication.ts";
import { OrgUnit } from "@stores/core/models/OrgUnit.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { PracOrgUnit } from "@stores/practice/models/PracOrgUnit.ts";
import { CheckboxField } from "@ui-components/form/CheckboxField.tsx";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { StaticPickerField } from "@ui-components/form/StaticPickerField.tsx";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";
import { WhenCountry } from "@ui-components/WhenCountry.tsx";

import { PracticeDetailsFormLabels } from "../practice-details/PracticeDetailsForm.types.tsx";
import { LOCATION_LIST_ID } from "../practice-locations/PracticeDetailsLocations.tsx";
import { LocationLabels } from "../PracticeScreen.types.ts";
import {
  LocationDetailsFormLabels,
  LocationDetailsValues,
  locationFormNameOf
} from "./LocationDetails.types.ts";
import { LocationDetailsFormAddressFields } from "./LocationDetailsFormAddressFields.tsx";
import { LocationDetailsFormFieldLabel } from "./LocationDetailsFormFieldLabel.tsx";
import { LocationDetailsFormValidator } from "./LocationDetailsFormValidator.ts";
import { MultiLocationsFields } from "./MultiLocationsFields.tsx";
import { getLocationFormInitialValues, onLocationFormSubmit } from "./utils.ts";

type LocationDetailsFormProps = {
  pracOrgUnit?: PracOrgUnit;
  locationOrgUnit?: OrgUnit;
};

export const LocationDetailsForm: React.FC<LocationDetailsFormProps> = observer(
  ({ pracOrgUnit, locationOrgUnit }) => {
    const root = useStores();
    const { core, routing } = root;
    const theme = useTheme();
    const { formFooter, formFields } = getUserStylesSet(theme);

    const readOnly = !core.hasPermissions(Permission.OrgUnitSettingWrite);
    const country = core.tenantDetails && core.tenantDetails.country;
    const { location: orgUnit } = core;
    const parentOrgUnitId = orgUnit?.parentOrgUnit?.id;
    const hasMultiLocationPermission = core.hasPermissions([
      Permission.PreReleaseMultiLocationAllowed
    ]);

    // Initialize validator
    const validator = new LocationDetailsFormValidator(core);
    const validateForm = (values: LocationDetailsValues) =>
      validator.validate(values);

    // Format phone numbers according to country
    const format = (value: any) =>
      formatCommunicationValue(value, CommunicationKind.Phone, country);

    const initialValues = getLocationFormInitialValues(
      { orgUnit, locationOrgUnit, pracOrgUnit },
      core
    );

    const onSubmit = async (values: LocationDetailsValues) =>
      onLocationFormSubmit(values, root, { initialValues, parentOrgUnitId });

    const isInactiveOrNewLocation =
      initialValues.isInactiveLocation || !initialValues.practiceName;

    const defaultLocationDisabled =
      initialValues.defaultLocation || isInactiveOrNewLocation;

    const defaultLocationTooltip = isInactiveOrNewLocation
      ? "Only active locations can be set as the default location"
      : undefined;

    return (
      <SubmissionForm<LocationDetailsValues>
        initialValues={initialValues}
        formName="location-details"
        onSubmit={onSubmit}
        autoFocus={false}
        readOnly={readOnly}
        disabled={locationOrgUnit?.isInactive}
        onSubmitSucceeded={() => {
          routing.push(routes.settings.practices.basePath.path({}));
        }}
        buttonsProps={{
          disableCancelOnPristine: true,
          onCancel: form => {
            form.reset();
            setTimeout(() => {
              routing.push({
                pathname: routes.settings.practices.basePath.path({}),
                hash: LOCATION_LIST_ID
              });
            }, 10); // timeout is required to allow time for the form to reset
          },
          styles: {
            root: formFooter
          }
        }}
        validate={validateForm}
      >
        {() => (
          <Stack
            styles={{
              root: formFields
            }}
            tokens={{ childrenGap: 8 }}
          >
            {hasMultiLocationPermission && (
              <Stack horizontal horizontalAlign="space-between">
                <Heading variant="section-heading-light">
                  {PracticeDetailsFormLabels.practiceLocation}
                </Heading>
              </Stack>
            )}
            <TextInputField
              onRenderLabel={() => (
                <LocationDetailsFormFieldLabel
                  label={LocationLabels.locationName}
                  tooltip={
                    hasMultiLocationPermission
                      ? "This name will be used for external communications (eg. appointment reminders)"
                      : undefined
                  }
                  nameOfField="practiceName"
                  disabled={readOnly}
                />
              )}
              label={LocationLabels.locationName}
              placeholder={LocationLabels.locationName}
              name={locationFormNameOf("practiceName")}
            />

            {hasMultiLocationPermission && <MultiLocationsFields />}
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <Stack.Item styles={{ root: { flex: 1 } }}>
                <TextInputField
                  placeholder={LocationLabels.phone}
                  name={locationFormNameOf("phone")}
                  label={LocationLabels.phone}
                  format={format}
                  required
                />
              </Stack.Item>
              <Stack.Item styles={{ root: { flex: 1 } }}>
                <TextInputField
                  placeholder={LocationLabels.email}
                  name={locationFormNameOf("email")}
                  label={LocationLabels.email}
                />
              </Stack.Item>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <Stack.Item styles={{ root: { flex: 1 } }}>
                <StaticPickerField
                  label={LocationLabels.timeZone}
                  name={locationFormNameOf("timezone")}
                  fetchDataSource={() =>
                    core.ref.timeZones.fetchAsKeyNameValues()
                  }
                  required
                />
              </Stack.Item>
              <WhenCountry is={Country.NewZealand}>
                <Stack.Item styles={{ root: { flex: 1 } }}>
                  <TextInputField
                    placeholder={LocationLabels.hpiFacilityId}
                    name={locationFormNameOf("hpiFacilityId")}
                    label={LocationLabels.hpiFacilityId}
                  />
                </Stack.Item>
              </WhenCountry>
            </Stack>

            <LocationDetailsFormAddressFields />
            {hasMultiLocationPermission && (
              <Fieldset legend={LocationLabels.defaultLocation}>
                <TooltipHost content={defaultLocationTooltip}>
                  <CheckboxField
                    disabled={defaultLocationDisabled}
                    label={LocationDetailsFormLabels.setDefaultLocation}
                    name={locationFormNameOf("defaultLocation")}
                  />
                </TooltipHost>
              </Fieldset>
            )}
          </Stack>
        )}
      </SubmissionForm>
    );
  }
);
